// 現在の潮の動き（歩合表示）
//
// 釣り用語「上げ N 分（いちぶ・にぶ…）」「下げ N 分」の歩合表示。
//   干潮 → 満潮 の所要時間を 10 等分した単位を「1分」とし、
//   経過時間が 30% に達したら「上げ 3 分」、70% で「下げ 7 分」など。
//   N は 0..10 の整数。3〜7 が潮流が速く釣りで活性が上がる時間帯とされる。

import type { TideResponse } from '@tideprecision/shared';

export type TideMotionKind = 'rising' | 'falling';

export interface TideMotion {
  kind: TideMotionKind;
  /** 0..10 の整数（歩合の N） */
  bu: number;
  /** 矢印アイコン */
  arrow: string;
  /** 表示ラベル ("上げ3分" 等) */
  label: string;
  /** デバッグ用: 直前極値からの経過分 / 全所要分 */
  elapsedMin: number;
  totalMin: number;
}

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

interface ExtremumPoint {
  minute: number;
  kind: 'high' | 'low';
}

function extractExtrema(data: TideResponse): ExtremumPoint[] {
  const [y, m, d] = data.date.split('-').map(Number) as [number, number, number];
  const startUtcMs = Date.UTC(y, m - 1, d) - JST_OFFSET_MS;
  return data.extrema
    .map((e) => ({
      minute: Math.round((new Date(e.time).getTime() - startUtcMs) / 60000),
      kind: e.kind,
    }))
    .sort((a, b) => a.minute - b.minute);
}

/**
 * 当日の潮汐データから現在時刻の潮の動きを歩合表示で返す。
 * 範囲外（前日／翌日表示中）は null。
 */
export function computeTideMotion(
  data: TideResponse,
  now: Date = new Date(),
): TideMotion | null {
  const [y, m, d] = data.date.split('-').map(Number) as [number, number, number];
  const startJstMs = Date.UTC(y, m - 1, d) - JST_OFFSET_MS;
  const elapsedMs = now.getTime() - startJstMs;
  const minute = Math.floor(elapsedMs / 60000);
  if (minute < 0 || minute >= data.levels.length) return null;

  const extrema = extractExtrema(data);

  // 現在時刻を挟む過去極値（直前）と未来極値（直後）を見つける
  const past = [...extrema].filter((e) => e.minute <= minute).pop() ?? null;
  const next = extrema.find((e) => e.minute > minute) ?? null;

  // 両方が揃わないと歩合計算できない → 外挿で N 分まで出す
  if (!past || !next) return extrapolateMotion(extrema, minute, data);

  const elapsed = minute - past.minute;
  const total = next.minute - past.minute;
  if (total <= 0) return extrapolateMotion(extrema, minute, data);

  // 直前極値が干潮 → 上げ潮、満潮 → 下げ潮
  const kind: TideMotionKind = past.kind === 'low' ? 'rising' : 'falling';

  // 歩合 N = 経過/所要 * 10、四捨五入で 0..10 に
  let bu = Math.round((elapsed / total) * 10);
  if (bu < 0) bu = 0;
  if (bu > 10) bu = 10;

  return {
    kind,
    bu,
    arrow: pickArrow(kind, bu),
    label: pickLabel(kind, bu),
    elapsedMin: elapsed,
    totalMin: total,
  };
}

/**
 * 表示ラベル:
 *   上げ0分（干潮直後）→ 「干潮」
 *   上げ10分（満潮直前）→ 「満潮」
 *   下げ0分（満潮直後）→ 「満潮」
 *   下げ10分（干潮直前）→ 「干潮」
 *   その他 → 「上げN分」「下げN分」
 */
function pickLabel(kind: TideMotionKind, bu: number): string {
  if (bu === 0) return kind === 'rising' ? '干潮' : '満潮';
  if (bu === 10) return kind === 'rising' ? '満潮' : '干潮';
  return `${kind === 'rising' ? '上げ' : '下げ'}${bu}分`;
}

/**
 * 矢印アイコン:
 *   - 満潮ピーク前後（上げ9分 / 満潮(上げ10分・下げ0分) / 下げ1分）は水平 ➡️
 *   - 干潮ピーク前後（下げ9分 / 干潮(下げ10分・上げ0分) / 上げ1分）は水平 ➡️
 *   - それ以外の上げ潮 ↗️、下げ潮 ↘️
 */
function pickArrow(kind: TideMotionKind, bu: number): string {
  const aroundHigh =
    (kind === 'rising' && (bu === 9 || bu === 10)) ||
    (kind === 'falling' && (bu === 0 || bu === 1));
  const aroundLow =
    (kind === 'falling' && (bu === 9 || bu === 10)) ||
    (kind === 'rising' && (bu === 0 || bu === 1));
  if (aroundHigh || aroundLow) return '➡️';
  return kind === 'rising' ? '↗️' : '↘️';
}

/** M2 主体の半日周潮の典型周期 [分]。極値間隔のフォールバック値として使う。 */
const TYPICAL_HALF_PERIOD_MIN = Math.round(6.21 * 60);

/**
 * 当日の極値間隔の中央値を返す。極値が 1 個以下なら典型値 (6.21h) を使う。
 * 「直前極値 → 直後極値」の所要時間 (total) として外挿で利用する。
 */
function inferHalfPeriodMin(extrema: ExtremumPoint[]): number {
  if (extrema.length < 2) return TYPICAL_HALF_PERIOD_MIN;
  const intervals: number[] = [];
  for (let i = 1; i < extrema.length; i++) {
    intervals.push(extrema[i]!.minute - extrema[i - 1]!.minute);
  }
  intervals.sort((a, b) => a - b);
  return intervals[Math.floor(intervals.length / 2)]!;
}

/**
 * 当日の最初の極値より前、または最後の極値より後で「次／前の極値」が
 * `data.extrema` (当日 1 日分) に含まれない場合の歩合外挿。
 *
 * - 最後の極値より後: 仮想的な次極値を `last + total` の位置に置き、`elapsed = now - last`
 * - 最初の極値より前: 仮想的な前極値を `first - total` の位置に置き、`elapsed = now - (first - total)`
 *
 * total は当日の極値間隔の中央値 (M2 主体ならほぼ 6.21h)。釣り用の歩合は 10% 単位なので
 * 数分のズレは N 分の四捨五入に吸収される。
 *
 * 当日の極値が 1 個も無い極端なケースのみ、傾きベースのフォールバック (bu=0) に落とす。
 */
function extrapolateMotion(
  extrema: ExtremumPoint[],
  minute: number,
  data: TideResponse,
): TideMotion {
  if (extrema.length === 0) return slopeOnlyFallback(data, minute);

  const total = inferHalfPeriodMin(extrema);
  const first = extrema[0]!;
  const last = extrema[extrema.length - 1]!;

  let kind: TideMotionKind;
  let elapsed: number;
  if (minute < first.minute) {
    // 当日の最初の極値より前: 仮想 past を first - total と仮定
    kind = first.kind === 'high' ? 'rising' : 'falling';
    elapsed = minute - (first.minute - total);
  } else {
    // 当日の最後の極値より後: 仮想 next を last + total と仮定
    kind = last.kind === 'low' ? 'rising' : 'falling';
    elapsed = minute - last.minute;
  }

  let bu = Math.round((elapsed / total) * 10);
  if (bu < 0) bu = 0;
  if (bu > 10) bu = 10;

  return {
    kind,
    bu,
    arrow: pickArrow(kind, bu),
    label: pickLabel(kind, bu),
    elapsedMin: elapsed,
    totalMin: total,
  };
}

/**
 * 当日に極値が 1 個も含まれない極端なケースの最終手段。
 * 傾きの符号だけで上げ／下げを表示し、bu は 0 にする（情報不足を示す）。
 */
function slopeOnlyFallback(data: TideResponse, minute: number): TideMotion {
  const i = Math.min(Math.max(minute, 1), data.levels.length - 2);
  const slope = (data.levels[i + 1]! - data.levels[i - 1]!) / 2;
  const kind: TideMotionKind = slope >= 0 ? 'rising' : 'falling';
  return {
    kind,
    bu: 0,
    arrow: kind === 'rising' ? '↗️' : '↘️',
    label: kind === 'rising' ? '上げ' : '下げ',
    elapsedMin: 0,
    totalMin: 0,
  };
}
